import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@rbx/ui';

interface TryItOutWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const LIST_ITEM_STYLES = { display: 'list-item', paddingLeft: 0 } as const;

/**
 * Warning dialog shown before executing MEDIUM and HIGH risk API operations.
 *
 * For manual testing in development, try these Legacy APIs > Users endpoints:
 * - MEDIUM risk: POST /v1/description
 * - HIGH risk: GET /v1/birthdate
 */
const TryItOutWarningDialog: FunctionComponent<TryItOutWarningDialogProps> = ({
  open,
  onClose,
  onContinue,
}) => {
  const { translate } = useTranslation();
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAcknowledged(event.target.checked);
  };

  const handleClose = () => {
    setIsAcknowledged(false);
    onClose();
  };

  const handleContinue = () => {
    setIsAcknowledged(false);
    onContinue();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='try-it-out-warning-dialog-title'
      aria-describedby='try-it-out-warning-dialog-description'
      maxWidth='Large'>
      <DialogTitle id='try-it-out-warning-dialog-title'>
        <Typography variant='h2'>{translate('Heading.TryItOutWarning')}</Typography>
      </DialogTitle>
      <Divider variant='middle' size='medium' />
      <DialogContent>
        <Grid container direction='column'>
          <Grid item>
            <Alert severity='warning' variant='filled' id='try-it-out-warning-dialog-description'>
              <Typography>{translate('Description.TryItOutWarningAlert')}</Typography>
            </Alert>
          </Grid>

          <Grid item sx={{ marginTop: 3, marginBottom: 1 }}>
            <Typography variant='body1'>{translate('Description.TryItOutWarningIntro')}</Typography>
          </Grid>

          <Grid item sx={{ marginBottom: 2 }}>
            <List sx={{ listStyleType: 'disc', paddingLeft: 2 }}>
              <ListItem sx={LIST_ITEM_STYLES}>
                <ListItemText primary={translate('Description.TryItOutWarningRisk1')} />
              </ListItem>
              <ListItem sx={LIST_ITEM_STYLES}>
                <ListItemText primary={translate('Description.TryItOutWarningRisk2')} />
              </ListItem>
              <ListItem sx={LIST_ITEM_STYLES}>
                <ListItemText primary={translate('Description.TryItOutWarningRisk3')} />
              </ListItem>
              <ListItem sx={LIST_ITEM_STYLES}>
                <ListItemText primary={translate('Description.TryItOutWarningRisk4')} />
              </ListItem>
            </List>
          </Grid>

          <Grid item sx={{ marginBottom: 3 }}>
            <Typography variant='body1'>
              {translate('Description.TryItOutWarningConclusion')}
            </Typography>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  inputProps={{
                    'aria-label': translate('Label.TryItOutWarningAcknowledge'),
                  }}
                  checked={isAcknowledged}
                  onChange={handleCheckboxChange}
                />
              }
              label={
                <Typography variant='body1'>
                  {translate('Label.TryItOutWarningAcknowledge')}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider variant='middle' size='medium' />
      <DialogActions>
        <Button onClick={handleClose} size='large' color='primary' variant='outlined'>
          {translate('Action.TryItOutWarningCancel')}
        </Button>
        <Button
          onClick={handleContinue}
          size='large'
          color='primaryBrand'
          disabled={!isAcknowledged}
          variant='contained'>
          {translate('Action.TryItOutWarningContinue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TryItOutWarningDialog;
